import { EffectiveInterestCalculator } from "./effective-interest";
/**
 * Finds list interest data by fixation period
 *
 * @param listInterestData List interest data for all clients fixation periods
 * @param fixationPeriod Fixation period
 * @returns List interest data for requested fixation period, undefined if missing
 */
export function getlistInterestDataByFixationPeriodId(listInterestData, fixationPeriod) {
    return listInterestData.find(({ id }) => id === fixationPeriod);
}
/**
 * Calculates discount rate from list interest data and current mortgage loan-to-value ratio
 *
 * @param listInterestData List interest data for chosen clients fixation period
 * @param loanToValueRatio Mortgage loan to value ratio
 * @returns List interest discount, zero if no discount
 */
function calculateDiscountRate(listInterestData, loanToValueRatio) {
    var _a;
    const sortedDiscountsData = listInterestData.discounts.sort((a, b) => a.maxLTV - b.maxLTV);
    const discount = sortedDiscountsData.find(({ maxLTV }) => loanToValueRatio <= maxLTV);
    return (_a = discount === null || discount === void 0 ? void 0 : discount.discount) !== null && _a !== void 0 ? _a : 0;
}
/**
 * @param listInterestRate List interest rate
 * @param discountRate discount rate
 * @returns Interest rate with discount
 */
function calculateInterestWithDiscount(listInterestRate, discountRate) {
    return listInterestRate - discountRate;
}
/**
 * Finds list interest rate by fixation period, calculate discount by current mortgage loan-to-value ratio and calculates effective interest rate
 *
 * @param listInterestData List interest data for all clients fixation periods
 * @param housingPrice Housing price
 * @param mortgageAmount Mortgage amount
 * @param customInterestRate Mortgage loan to value ratio
 * @param fixationPeriodId Fixation period id
 * @param membershipDiscount If set the interest rate is reduced with the specified discount rate
 * @returns List interest rate, interest with discount rate, discount rate and effective interest rate
 */
export function getBestInterestDataByFixationPeriodId(listInterestData, housingPrice, mortgageAmount, customInterestRate, fixationPeriodId, membershipDiscount) {
    const listInterestDataByPeriod = getlistInterestDataByFixationPeriodId(listInterestData, fixationPeriodId);
    const loanToValueRatio = mortgageAmount / housingPrice;
    if (!listInterestDataByPeriod) {
        return {
            interestWithDiscount: customInterestRate,
            loanToValueDiscount: 0,
            membershipDiscount: 0,
            effectiveInterest: EffectiveInterestCalculator.withInterest(customInterestRate),
            listInterest: -1,
            period: "",
        };
    }
    const { listInterest, period } = listInterestDataByPeriod;
    const loanToValueDiscount = calculateDiscountRate(listInterestDataByPeriod, loanToValueRatio);
    const calculationMembershipDiscount = membershipDiscount ? Math.min(membershipDiscount, listInterest - loanToValueDiscount) : 0;
    const totalDiscount = Math.min(loanToValueDiscount + calculationMembershipDiscount, listInterest);
    const interestWithDiscount = calculateInterestWithDiscount(listInterest, totalDiscount);
    const effectiveInterest = EffectiveInterestCalculator.withInterest(interestWithDiscount);
    return {
        interestWithDiscount,
        loanToValueDiscount,
        membershipDiscount: calculationMembershipDiscount,
        effectiveInterest,
        listInterest,
        period,
    };
}
/**
 * @param listInterestData List interest data for all clients fixation periods
 * @param listInterestId Fixation period id
 * @returns List interest rate for requested fixation period
 */
export function getListInterestRateByFixationPeriodId(listInterestData, listInterestId) {
    const curentListInterestData = listInterestData.find((data) => data.id === listInterestId);
    if (!curentListInterestData) {
        return -1;
    }
    return curentListInterestData.listInterest;
}
/**
 * Calculates best interest rate with discount depending on loan-to-value ratio
 *
 * @param listInterestData List interest data for one fixation period
 * @param loanToValueRatio Loan-to-value ratio
 * @returns List interest fixation period id for best interest and best interest
 */
function calculateBestInterestByLTV(listInterestData, loanToValueRatio) {
    const discount = calculateDiscountRate(listInterestData, loanToValueRatio);
    return {
        id: listInterestData.id,
        interestWithDiscount: listInterestData.listInterest - discount,
    };
}
/**
 * Determines list interest fixation period id at best interest depending on loan-to-value ratio
 *
 * @param listInterestData List interest data for all clients fixation periods
 * @param loanToValueRatio Loan-to-value ratio
 * @returns List interest fixation period id for best interest
 */
function getListInterestIdAtBestInterest(listInterestData, loanToValueRatio) {
    const bestListInterest = listInterestData.reduce((prev, curr) => {
        const bestInterest = calculateBestInterestByLTV(curr, loanToValueRatio);
        return prev.interestWithDiscount <= bestInterest.interestWithDiscount ? prev : bestInterest;
    }, { id: "", interestWithDiscount: Number.MAX_SAFE_INTEGER });
    return bestListInterest.id;
}
/**
 * Determines best interest data depending on loan-to-value ratio
 *
 * @param listInterestData List interest data for all clients fixation periods
 * @param housingPrice Housing price
 * @param mortgageAmount Mortgage amount
 * @returns Best interest data
 */
export function getBestInterestDataByLTV(listInterestData, housingPrice, mortgageAmount) {
    if (listInterestData.length === 0) {
        return {
            listInterest: -1,
            interestWithDiscount: 0,
            membershipDiscount: 0,
            loanToValueDiscount: 0,
            effectiveInterest: 0,
            period: "",
        };
    }
    const loanToValueRatio = mortgageAmount / housingPrice;
    const listInterestIdWithBestInterest = getListInterestIdAtBestInterest(listInterestData, loanToValueRatio);
    const bestInterestData = getBestInterestDataByFixationPeriodId(listInterestData, housingPrice, mortgageAmount, 0, listInterestIdWithBestInterest);
    return bestInterestData;
}
